import React, { useState, useEffect } from 'react';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import PageHeader from '../components/Common/PageHeader';
import { TableSkeleton } from '../components/Common/SkeletonLoader';
import apiService from '../services/api';

const LocationsView = ({ showNotification }) => {
  // Local slideout state
  const [slideoutState, setSlideoutState] = useState({
    isOpen: false,
    title: '',
    content: null,
    width: 'medium'
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch locations from API
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setApiError(false);
      const response = await apiService.getOffices();
      const apiLocations = response.map(transformApiLocation);
      setLocations(apiLocations);
    } catch (error) {
      setLocations([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  // Transform API office data to match frontend location structure
  const transformApiLocation = (apiOffice) => ({
    id: apiOffice.id,
    name: apiOffice.name || 'Unknown Location',
    locationType: apiOffice.locationTypeStr || (apiOffice.locationType === 1 ? 'online' : apiOffice.locationType === 2 ? 'hybrid' : 'physical'),
    onlinePlatform: apiOffice.onlinePlatform || '',
    meetingUrl: apiOffice.meetingUrl || '',
    address: {
      street: apiOffice.address || '',
      city: apiOffice.city || '',
      postcode: apiOffice.postalCode || ''
    },
    phone: apiOffice.phone || '',
    email: apiOffice.email || '',
    status: apiOffice.status === 0 ? 'active' : 'inactive',
    facilities: {
      treatmentRooms: 1,
      parking: false,
      wheelchairAccess: false,
      wifi: false,
      reception: false,
      waitingArea: false
    },
    businessDetails: {
      openingHours: '',
      daysOpen: ''
    },
    contactPerson: {
      name: '',
      phone: ''
    },
    assignedPractitioners: [],
    supportStaff: 0,
    performance: {
      totalBookings: 0,
      monthlyRevenue: 0,
      utilizationRate: 0
    },
    specialInstructions: apiOffice.description || ''
  });

  // Form handling
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    locationType: 'physical',
    onlinePlatform: '',
    meetingUrl: '',
    address: {
      street: '',
      city: '',
      postcode: ''
    },
    phone: '',
    email: '',
    facilities: {
      treatmentRooms: 1,
      parking: false,
      wheelchairAccess: false,
      wifi: false,
      reception: false,
      waitingArea: false
    },
    businessDetails: {
      openingHours: '',
      daysOpen: ''
    },
    contactPerson: {
      name: '',
      phone: ''
    },
    specialInstructions: ''
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      locationType: 'physical',
      onlinePlatform: '',
      meetingUrl: '',
      address: {
        street: '',
        city: '',
        postcode: ''
      },
      phone: '',
      email: '',
      facilities: {
        treatmentRooms: 1,
        parking: false,
        wheelchairAccess: false,
        wifi: false,
        reception: false,
        waitingArea: false
      },
      businessDetails: {
        openingHours: '',
        daysOpen: ''
      },
      contactPerson: {
        name: '',
        phone: ''
      },
      specialInstructions: ''
    });
    setEditingLocation(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return; // Prevent double submission

    try {
      setSubmitting(true);

      if (editingLocation) {
        // Transform form data to API format
        const apiData = transformFormToApi(formData);
        await apiService.updateOffice(editingLocation.id, apiData);

        // Update local state optimistically
        setLocations(prev => prev.map(loc =>
          loc.id === editingLocation.id
            ? {
                ...loc,
                ...formData,
                status: loc.status,
                id: loc.id,
                performance: loc.performance,
                assignedPractitioners: loc.assignedPractitioners,
                supportStaff: loc.supportStaff
              }
            : loc
        ));
        showNotification('Location updated successfully', 'success');
      } else {
        // Transform form data to API format
        const apiData = transformFormToApi(formData);
        const newOffice = await apiService.createOffice(apiData);

        // Add new location to local state
        const newLocation = transformApiLocation(newOffice);
        setLocations(prev => [...prev, newLocation]);
        showNotification('Location created successfully', 'success');
      }

      resetForm();
      setSlideoutState(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
      console.error('Error saving location:', error);
      showNotification(
        editingLocation ? 'Failed to update location' : 'Failed to create location',
        'error'
      );
      // Don't close slideout on error
    } finally {
      setSubmitting(false);
    }
  };

  // Transform form data to API format
  const platformToLocationTypeInt = (locationType) => {
    if (locationType === 'online') return 1;
    if (locationType === 'hybrid') return 2;
    return 0;
  };

  const transformFormToApi = (formData) => ({
    name: formData.name,
    description: formData.specialInstructions,
    address: formData.address.street,
    city: formData.address.city,
    postalCode: formData.address.postcode,
    country: 'UK',
    phone: formData.phone,
    email: formData.email,
    status: 0,
    locationType: platformToLocationTypeInt(formData.locationType),
    onlinePlatform: formData.onlinePlatform || null,
    meetingUrl: formData.meetingUrl || null
  });

  const openCreateForm = () => {
    resetForm();
    setSlideoutState({
      isOpen: true,
      title: 'Create New Location',
      content: 'form',
      width: 'large'
    });
  };

  const openEditForm = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      locationType: location.locationType || 'physical',
      onlinePlatform: location.onlinePlatform || '',
      meetingUrl: location.meetingUrl || '',
      address: location.address,
      phone: location.phone,
      email: location.email,
      facilities: location.facilities,
      businessDetails: location.businessDetails,
      contactPerson: location.contactPerson,
      specialInstructions: location.specialInstructions || ''
    });
    setSlideoutState({
      isOpen: true,
      title: 'Edit Location',
      content: 'form',
      width: 'large'
    });
  };

  const viewLocationDetails = (location) => {
    setSlideoutState({
      isOpen: true,
      title: location.name,
      content: 'detail',
      width: 'large'
    });
    setEditingLocation(location);
  };

  const closeSlideout = () => {
    setSlideoutState(prev => ({ ...prev, isOpen: false }));
    setSubmitting(false);
    resetForm();
  };

  const deleteLocation = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await apiService.deleteOffice(locationId);
        setLocations(prev => prev.filter(loc => loc.id !== locationId));
        showNotification('Location deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting location:', error);
        showNotification('Failed to delete location', 'error');
      }
    }
  };

  // Filtering logic
  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.address.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || location.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="locations-view">
      <PageHeader pageKey="locations">
        <button className="btn btn-primary" onClick={openCreateForm}>
          <span className="icon icon--add icon--success"></span>
          Add New Location
        </button>
      </PageHeader>

      {/* Status Filter */}
      <div className="material-filters-section">
        <div className="material-filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="material-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : apiError ? (
        <div className="api-error-state">
          <div className="api-error-state__icon">⚠️</div>
          <h3>Could not load locations</h3>
          <p>There was a problem connecting to the server. Please check your connection and try again.</p>
          <button className="btn btn-primary" onClick={fetchLocations}>Retry</button>
        </div>
      ) : viewMode === 'table' ? (
        <div className="material-table-container">
          <div className="material-table-header">
            <h3 className="material-table-title">Location Overview</h3>
            <span className="material-table-meta">Showing {filteredLocations.length} of {locations.length} locations</span>
          </div>

          <div className="material-table-controls">
            <div className="material-search">
              <input
                type="text"
                className="material-search-input"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="material-view-toggle">
              <button
                className={`material-chip ${viewMode === 'table' ? 'selected' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
              <button
                className={`material-chip ${viewMode === 'cards' ? 'selected' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                Cards
              </button>
            </div>
          </div>

          <table className="material-table">
            <thead>
              <tr>
                <th className="material-cell material-cell--header">Location</th>
                <th className="material-cell material-cell--header">Address</th>
                <th className="material-cell material-cell--header">Contact</th>
                <th className="material-cell material-cell--header">Status</th>
                <th className="material-cell material-cell--header">Revenue</th>
                <th className="material-cell material-cell--header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.map((location) => (
                <tr key={location.id} className="material-table-row">
                  <td className="material-cell">
                    <div className="material-location-cell">
                      <div className="material-avatar">
                        {location.name ? location.name.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase() : 'UL'}
                      </div>
                      <div className="material-cell-info">
                        <h4>{location.name}</h4>
                        <div className="material-cell-meta">
                          {location.locationType === 'online'
                            ? `💻 ${location.onlinePlatform || 'Online'}`
                            : [location.address.city, location.address.postcode].filter(Boolean).join(', ') || 'No address'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="material-cell">
                    <div className="material-cell-content">
                      {location.locationType === 'online' ? (
                        <span className="material-body-medium">
                          {location.meetingUrl
                            ? <a href={location.meetingUrl} target="_blank" rel="noreferrer" style={{color:'#0e73e4'}}>Join link ↗</a>
                            : <em style={{color:'#999'}}>No link set</em>}
                        </span>
                      ) : (
                        <>
                          <span className="material-body-medium">{location.address.city}</span>
                          <span className="material-body-small material-text-secondary">{location.address.postcode}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="material-cell">
                    <div className="material-contact-info">
                      <div className="material-contact-primary">{location.phone || (location.locationType === 'online' ? location.onlinePlatform : '')}</div>
                      <div className="material-contact-secondary">{location.email}</div>
                    </div>
                  </td>
                  <td className="material-cell">
                    <span className={`material-chip--${location.status === 'active' ? 'success' : 'error'}`}>
                      {location.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="material-cell">
                    <span className="material-body-medium">£{location.performance.monthlyRevenue.toLocaleString()}</span>
                  </td>
                  <td className="actions-cell">
                    <div className="action-icons">
                      <button
                        className="action-icon action-icon--view"
                        onClick={() => viewLocationDetails(location)}
                        data-tooltip="View Details"
                      ></button>
                      <button
                        className="action-icon action-icon--edit"
                        onClick={() => openEditForm(location)}
                        data-tooltip="Edit Location"
                      ></button>
                      <button
                        className="action-icon action-icon--delete"
                        onClick={() => deleteLocation(location.id)}
                        data-tooltip="Delete Location"
                      ></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredLocations.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <h3>{searchTerm || statusFilter ? 'No matches found' : 'No locations yet'}</h3>
              <p>{searchTerm || statusFilter ? 'Try adjusting your search or filter criteria.' : 'Add your first clinic location to get started.'}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="material-cards-grid">
          {filteredLocations.map((location) => (
            <div key={location.id} className="material-card">
              <div className="material-card-header">
                <h3 className="material-title-medium">{location.name}</h3>
                <span className={`material-chip material-chip--${location.status === 'active' ? 'success' : 'error'}`}>
                  {location.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="material-card-content">
                <div className="material-card-section">
                  <p className="material-body-medium">
                    {location.address.street}<br/>
                    {location.address.city} {location.address.postcode}
                  </p>
                  <p className="material-body-small material-text-secondary">
                    📞 {location.phone}<br/>
                    ✉️ {location.email}
                  </p>
                </div>

                <div className="material-card-stats">
                  <div className="material-stat-item">
                    <span className="material-body-small material-text-secondary">Treatment Rooms</span>
                    <span className="material-title-small">{location.facilities.treatmentRooms}</span>
                  </div>
                  <div className="material-stat-item">
                    <span className="material-body-small material-text-secondary">Monthly Revenue</span>
                    <span className="material-title-small">£{location.performance.monthlyRevenue.toLocaleString()}</span>
                  </div>
                </div>

                <div className="material-chip-group">
                  {location.facilities.parking && <span className="material-chip material-chip--info">🅿️ Parking</span>}
                  {location.facilities.wheelchairAccess && <span className="material-chip material-chip--info">♿ Access</span>}
                  {location.facilities.wifi && <span className="material-chip material-chip--info">📶 WiFi</span>}
                </div>
              </div>

              <div className="material-card-actions">
                <button
                  className="material-button-outlined"
                  onClick={() => viewLocationDetails(location)}
                >
                  View Details
                </button>
                <button
                  className="material-button-filled"
                  onClick={() => openEditForm(location)}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}

          {filteredLocations.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <h3>{searchTerm || statusFilter ? 'No matches found' : 'No locations yet'}</h3>
              <p>{searchTerm || statusFilter ? 'Try adjusting your search or filter criteria.' : 'Add your first clinic location to get started.'}</p>
            </div>
          )}
        </div>
      )}

      <SlideoutPanel
        isOpen={slideoutState.isOpen}
        onClose={closeSlideout}
        title={slideoutState.title}
        width={slideoutState.width === 'large' ? '750px' : slideoutState.width === 'medium' ? '600px' : '400px'}
      >
        {slideoutState.content === 'form' && (
          <LocationForm
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            onCancel={closeSlideout}
            isEditing={!!editingLocation}
            submitting={submitting}
          />
        )}
        
        {slideoutState.content === 'detail' && editingLocation && (
          <LocationDetail
            location={editingLocation}
            onEdit={() => openEditForm(editingLocation)}
          />
        )}
      </SlideoutPanel>
    </div>
  );
};

// Location Form Component
const ONLINE_PLATFORMS = [
  { value: 'Zoom',        label: '📹 Zoom' },
  { value: 'Teams',       label: '💼 Microsoft Teams' },
  { value: 'Meet',        label: '📞 Google Meet' },
  { value: 'Phone',       label: '📱 Phone Call' },
];

const LocationForm = ({ formData, updateFormData, onSubmit, onCancel, isEditing, submitting }) => {
  const isOnline = formData.locationType === 'online';
  const isHybrid = formData.locationType === 'hybrid';
  const needsAddress = !isOnline;
  const needsMeeting = isOnline || isHybrid;

  return (
    <form onSubmit={onSubmit} className="location-form">
      <div className="form-content">
        {/* Basic Information */}
        <div className="form-section">
          <h4 className="section-title">Basic Information</h4>
          <div className="form-grid">
            <div className="form-group full-width">
              <label className="form-label">Location Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="e.g., Harley Street Clinic"
                required
              />
            </div>

            <div className="form-group full-width">
              <label className="form-label">Location Type *</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { value: 'physical', label: '🏥 In-Person' },
                  { value: 'online',   label: '💻 Online Only' },
                  { value: 'hybrid',   label: '🔀 Hybrid' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateFormData('locationType', opt.value)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '2px solid',
                      borderColor: formData.locationType === opt.value ? '#0e73e4' : '#ddd',
                      background: formData.locationType === opt.value ? '#e8f0fe' : '#fff',
                      color: formData.locationType === opt.value ? '#0e73e4' : '#555',
                      fontWeight: formData.locationType === opt.value ? 600 : 400,
                      cursor: 'pointer'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {needsMeeting && (
              <>
                <div className="form-group">
                  <label className="form-label">Platform *</label>
                  <select
                    className="form-input"
                    value={formData.onlinePlatform}
                    onChange={(e) => updateFormData('onlinePlatform', e.target.value)}
                    required={isOnline}
                  >
                    <option value="">Select platform</option>
                    {ONLINE_PLATFORMS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Meeting Link</label>
                  <input
                    type="url"
                    className="form-input"
                    value={formData.meetingUrl}
                    onChange={(e) => updateFormData('meetingUrl', e.target.value)}
                    placeholder="https://zoom.us/j/123456789"
                  />
                </div>
              </>
            )}

            {!isOnline && (
              <>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    placeholder="+44 20 7123 4567"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="contact@clinic.com"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Address — only for physical / hybrid */}
        {needsAddress && (
        <div className="form-section">
          <h4 className="section-title">Address</h4>
          <div className="form-grid">
            <div className="form-group full-width">
              <label className="form-label">Street Address</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.street}
                onChange={(e) => updateFormData('address.street', e.target.value)}
                placeholder="123 Main Street"
              />
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.city}
                onChange={(e) => updateFormData('address.city', e.target.value)}
                placeholder="London"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Postcode</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.postcode}
                onChange={(e) => updateFormData('address.postcode', e.target.value)}
                placeholder="SW1A 1AA"
              />
            </div>
          </div>
        </div>
        )}

        {/* Facilities */}
        <div className="form-section">
          <h4 className="section-title">Facilities</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Treatment Rooms</label>
              <input
                type="number"
                min="1"
                className="form-input"
                value={formData.facilities.treatmentRooms}
                onChange={(e) => updateFormData('facilities.treatmentRooms', parseInt(e.target.value))}
                required
              />
            </div>
            
            <div className="form-group">
            </div>
          </div>
          
          <div className="checkbox-grid">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.facilities.parking}
                onChange={(e) => updateFormData('facilities.parking', e.target.checked)}
              />
              <span>Parking Available</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.facilities.wheelchairAccess}
                onChange={(e) => updateFormData('facilities.wheelchairAccess', e.target.checked)}
              />
              <span>Wheelchair Access</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.facilities.wifi}
                onChange={(e) => updateFormData('facilities.wifi', e.target.checked)}
              />
              <span>WiFi Available</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.facilities.reception}
                onChange={(e) => updateFormData('facilities.reception', e.target.checked)}
              />
              <span>Reception Area</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.facilities.waitingArea}
                onChange={(e) => updateFormData('facilities.waitingArea', e.target.checked)}
              />
              <span>Waiting Area</span>
            </label>
          </div>
        </div>

        {/* Business Details */}
        <div className="form-section">
          <h4 className="section-title">Business Details</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Opening Hours</label>
              <input
                type="text"
                className="form-input"
                value={formData.businessDetails.openingHours}
                onChange={(e) => updateFormData('businessDetails.openingHours', e.target.value)}
                placeholder="8:00 AM - 6:00 PM"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Days Open</label>
              <input
                type="text"
                className="form-input"
                value={formData.businessDetails.daysOpen}
                onChange={(e) => updateFormData('businessDetails.daysOpen', e.target.value)}
                placeholder="Monday - Friday"
              />
            </div>
          </div>
        </div>

        {/* Contact Person */}
        <div className="form-section">
          <h4 className="section-title">Contact Person</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Manager Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.contactPerson.name}
                onChange={(e) => updateFormData('contactPerson.name', e.target.value)}
                placeholder="e.g., Sarah Manager"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Direct Phone</label>
              <input
                type="tel"
                className="form-input"
                value={formData.contactPerson.phone}
                onChange={(e) => updateFormData('contactPerson.phone', e.target.value)}
                placeholder="+44 20 7123 4568"
              />
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        <div className="form-section">
          <h4 className="section-title">Special Instructions</h4>
          <div className="form-group">
            <label className="form-label">Access Instructions</label>
            <textarea
              className="form-input"
              value={formData.specialInstructions}
              onChange={(e) => updateFormData('specialInstructions', e.target.value)}
              placeholder="Entry instructions, parking details, accessibility information..."
              rows="3"
            />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : (isEditing ? 'Update Location' : 'Create Location')}
        </button>
      </div>
    </form>
  );
};

// Location Detail Component
const LocationDetail = ({ location, onEdit }) => {
  return (
    <div className="location-detail">
      <div className="detail-sections">
        {/* Basic Info */}
        <div className="detail-section">
          <h4>Basic Information</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Location ID</label>
              <span>{location.id}</span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <span className={`status-badge status-${location.status}`}>
                {location.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Address & Contact */}
        <div className="detail-section">
          <h4>Address & Contact</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Address</label>
              <span>
                {location.address.street}<br />
                {location.address.city} {location.address.postcode}
              </span>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <span>{location.phone}</span>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <span>{location.email}</span>
            </div>
          </div>
        </div>

        {/* Facilities */}
        <div className="detail-section">
          <h4>Facilities</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Treatment Rooms</label>
              <span>{location.facilities.treatmentRooms}</span>
            </div>
            <div className="detail-item">
              <label>Support Staff</label>
              <span>{location.supportStaff}</span>
            </div>
          </div>
          
          <div className="facilities-list">
            <label>Available Facilities</label>
            <div className="facility-tags">
              {location.facilities.parking && <span className="facility-tag">🅿️ Parking</span>}
              {location.facilities.wheelchairAccess && <span className="facility-tag">♿ Wheelchair Access</span>}
              {location.facilities.wifi && <span className="facility-tag">📶 WiFi</span>}
              {location.facilities.reception && <span className="facility-tag">🏪 Reception</span>}
              {location.facilities.waitingArea && <span className="facility-tag">🪑 Waiting Area</span>}
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className="detail-section">
          <h4>Performance Metrics</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Total Bookings</label>
              <span>{location.performance.totalBookings.toLocaleString()}</span>
            </div>
            <div className="detail-item">
              <label>Monthly Revenue</label>
              <span>£{location.performance.monthlyRevenue.toLocaleString()}</span>
            </div>
            <div className="detail-item">
              <label>Utilization Rate</label>
              <span>{location.performance.utilizationRate}%</span>
            </div>
          </div>
        </div>

        {/* Practitioners */}
        <div className="detail-section">
          <h4>Assigned Practitioners</h4>
          <div className="practitioners-list">
            {location.assignedPractitioners.map((practitioner, index) => (
              <span key={index} className="practitioner-tag">{practitioner}</span>
            ))}
          </div>
        </div>

        {/* Special Instructions */}
        {location.specialInstructions && (
          <div className="detail-section">
            <h4>Special Instructions</h4>
            <p>{location.specialInstructions}</p>
          </div>
        )}
      </div>

      <div className="detail-actions">
        <button className="btn btn-primary" onClick={onEdit}>
          Edit Location
        </button>
      </div>
    </div>
  );
};

export default LocationsView;